import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  ButtonGroup,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ControllerWidget from './ControllerWidget';
import DetailPanel from './DetailPanel';
import AddNaptarBejegyzesDialog from './AddNaptarBejegyzesDialog';
import { NaptarBejegyzesDto } from '../services/naptarServiceTypes';
import { getApiErrorMessage } from '../services/apiService';

interface CalendarService {
  getByDateRange: (startDate: string, endDate: string) => Promise<NaptarBejegyzesDto[]>;
  create: (payload: Partial<NaptarBejegyzesDto>) => Promise<NaptarBejegyzesDto>;
  update: (id: number, payload: Partial<NaptarBejegyzesDto>) => Promise<NaptarBejegyzesDto>;
  delete: (id: number) => Promise<void>;
}

type ViewMode = 'heti' | 'havi';

interface NaptarBejegyzesekContentProps {
  title: string;
  selectedMenu: string;
  service: CalendarService;
  createPermission: string;
  editPermission: string;
  deletePermission: string;
  addDialogTitle: string;
  defaultStartTime?: string;
  defaultEndTime?: string;
}

const NaptarBejegyzesekContent: React.FC<NaptarBejegyzesekContentProps> = ({
  title,
  selectedMenu,
  service,
  createPermission,
  editPermission,
  deletePermission,
  addDialogTitle,
  defaultStartTime,
  defaultEndTime,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('heti');
  const [periodAnchorDate, setPeriodAnchorDate] = useState<Date>(new Date());
  const [entries, setEntries] = useState<NaptarBejegyzesDto[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<NaptarBejegyzesDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState<{ column: string; value: string } | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const padLeft = (input: number | string, length: number): string => {
    const text = String(input);
    if (text.length >= length) {
      return text;
    }

    return `${new Array(length - text.length + 1).join('0')}${text}`;
  };

  const formatDateToApi = (date: Date): string => {
    const year = date.getFullYear();
    const month = padLeft(date.getMonth() + 1, 2);
    const day = padLeft(date.getDate(), 2);
    return `${year}-${month}-${day}`;
  };

  const getWeekBounds = (anchor: Date): { start: Date; end: Date } => {
    const start = new Date(anchor);
    const dayOfWeek = start.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    start.setDate(start.getDate() - daysFromMonday);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  };

  const getMonthBounds = (anchor: Date): { start: Date; end: Date } => {
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    start.setHours(0, 0, 0, 0);

    const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  };

  const toLocalDateKey = (value: string | Date): string => {
    const sourceDate = value instanceof Date ? value : new Date(value);
    const year = sourceDate.getFullYear();
    const month = padLeft(sourceDate.getMonth() + 1, 2);
    const day = padLeft(sourceDate.getDate(), 2);
    return `${year}-${month}-${day}`;
  };

  const normalizeTime = (value: string): string => {
    if (!value) {
      return '';
    }

    const match = value.match(/^(\d{2}:\d{2})/);
    return match ? match[1] : value;
  };

  const loadPeriodData = async (mode: ViewMode, anchorDate: Date) => {
    setLoading(true);
    setError(null);

    try {
      const { start, end } = mode === 'heti' ? getWeekBounds(anchorDate) : getMonthBounds(anchorDate);
      const data = await service.getByDateRange(formatDateToApi(start), formatDateToApi(end));
      setEntries(data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Hiba történt az adatok betöltése során.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPeriodData(viewMode, periodAnchorDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, periodAnchorDate]);

  useEffect(() => {
    if (!searchFilter || !searchFilter.value) {
      setFilteredEntries(entries);
      return;
    }

    const value = searchFilter.value.toLowerCase();
    const filtered = entries.filter((entry) => {
      if (!searchFilter.column) {
        return Object.values(entry).some((cell) => String(cell ?? '').toLowerCase().includes(value));
      }

      return String((entry as any)[searchFilter.column] ?? '').toLowerCase().includes(value);
    });

    setFilteredEntries(filtered);
  }, [entries, searchFilter]);

  const periodDays = useMemo(() => {
    if (viewMode === 'heti') {
      const { start, end } = getWeekBounds(periodAnchorDate);
      const days: { date: Date; inCurrentPeriod: boolean }[] = [];
      const current = new Date(start);

      while (current <= end) {
        days.push({ date: new Date(current), inCurrentPeriod: true });
        current.setDate(current.getDate() + 1);
      }

      return days;
    }

    const { start: monthStart, end: monthEnd } = getMonthBounds(periodAnchorDate);
    const calendarStart = new Date(monthStart);
    const mondayBasedIndex = calendarStart.getDay() === 0 ? 6 : calendarStart.getDay() - 1;
    calendarStart.setDate(calendarStart.getDate() - mondayBasedIndex);

    const calendarEnd = new Date(monthEnd);
    const sundayBasedIndex = calendarEnd.getDay();
    const daysToSunday = sundayBasedIndex === 0 ? 0 : 7 - sundayBasedIndex;
    calendarEnd.setDate(calendarEnd.getDate() + daysToSunday);

    const days: { date: Date; inCurrentPeriod: boolean }[] = [];
    const current = new Date(calendarStart);
    current.setHours(0, 0, 0, 0);
    calendarEnd.setHours(23, 59, 59, 999);

    while (current <= calendarEnd) {
      days.push({
        date: new Date(current),
        inCurrentPeriod: current.getMonth() === periodAnchorDate.getMonth(),
      });
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [viewMode, periodAnchorDate]);

  const groupedPeriodData = useMemo(() => {
    const groups: Record<string, NaptarBejegyzesDto[]> = {};

    filteredEntries.forEach((entry) => {
      const key = toLocalDateKey(entry.datum);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(entry);
    });

    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEntries]);

  const periodTitle = useMemo(() => {
    if (viewMode === 'heti') {
      const { start, end } = getWeekBounds(periodAnchorDate);
      return `${start.toLocaleDateString('hu-HU')} - ${end.toLocaleDateString('hu-HU')}`;
    }

    return periodAnchorDate.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' });
  }, [viewMode, periodAnchorDate]);

  const handleSearch = (column: string, value: string) => {
    if (!value.trim()) {
      setSearchFilter(null);
      return;
    }

    setSearchFilter({ column, value });
  };

  const handleGetSuggestions = async (searchValue: string): Promise<string[]> => {
    if (searchValue.length < 2) {
      return [];
    }

    const lower = searchValue.toLowerCase();
    const values = new Set<string>();

    entries.forEach((entry) => {
      Object.values(entry).forEach((cell) => {
        if (cell == null) {
          return;
        }

        const stringValue = String(cell);
        if (stringValue.toLowerCase().includes(lower)) {
          values.add(stringValue);
        }
      });
    });

    return Array.from(values).slice(0, 10);
  };

  const handlePeriodShift = (direction: -1 | 1) => {
    setPeriodAnchorDate((previous) => {
      const next = new Date(previous);
      if (viewMode === 'heti') {
        next.setDate(next.getDate() + direction * 7);
      } else {
        next.setMonth(next.getMonth() + direction);
      }
      return next;
    });
  };

  const normalizePayload = (payload: any): Partial<NaptarBejegyzesDto> => {
    const toLocalDateTimeString = (value: any): string => {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return String(value);
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}T12:00:00`;
    };

    return {
      datum: toLocalDateTimeString(payload.datum),
      kezdesIdo: normalizeTime(payload.kezdesIdo),
      befejezesIdo: normalizeTime(payload.befejezesIdo),
      leiras: String(payload.leiras ?? '').trim(),
    };
  };

  const handleCreate = async (payload: { datum: string; kezdesIdo: string; befejezesIdo: string; leiras: string }) => {
    try {
      await service.create(payload);
      await loadPeriodData(viewMode, periodAnchorDate);
    } catch (error) {
      setError(getApiErrorMessage(error, 'Nem sikerült létrehozni a bejegyzést.'));
      throw error;
    }
  };

  const handleEdit = async (payload: any) => {
    try {
      await service.update(payload.id, normalizePayload(payload));
      await loadPeriodData(viewMode, periodAnchorDate);
      setIsPanelOpen(false);
    } catch (error) {
      setError(getApiErrorMessage(error, 'Nem sikerült frissíteni a bejegyzést.'));
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!selectedItem?.id) {
      return;
    }

    try {
      await service.delete(selectedItem.id);
      await loadPeriodData(viewMode, periodAnchorDate);
      setIsPanelOpen(false);
      setSelectedItem(null);
    } catch (error) {
      setError(getApiErrorMessage(error, 'Nem sikerült törölni a bejegyzést.'));
      throw error;
    }
  };

  const formatColumnName = (key: string): string => {
    const labels: Record<string, string> = {
      datum: 'Dátum',
      kezdesIdo: 'Kezdési idő',
      befejezesIdo: 'Befejezési idő',
      leiras: 'Leírás',
      felhasznaloNev: 'Felhasználó',
      userId: 'Felhasználó azonosító',
    };

    return labels[key] || key;
  };

  const formatCellValue = (key: string, value: any): string => {
    if (value == null) {
      return '';
    }

    if (key === 'datum') {
      return new Date(value).toLocaleDateString('hu-HU');
    }

    if (key === 'kezdesIdo' || key === 'befejezesIdo') {
      return normalizeTime(String(value));
    }

    return String(value);
  };

  const tableColumns = entries.length > 0
    ? Object.keys(entries[0])
    : ['datum', 'kezdesIdo', 'befejezesIdo', 'leiras', 'felhasznaloNev'];

  const calendarWeekDayLabels = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ControllerWidget
        selectedMenu={selectedMenu}
        tableColumns={tableColumns}
        onSearch={handleSearch}
        onVisibleColumnsChange={() => {}}
        onAddRecord={() => setAddDialogOpen(true)}
        onGetSuggestions={handleGetSuggestions}
        createPermission={createPermission}
      />

      <Card>
        <CardContent>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="h2">
              {title} - {viewMode === 'heti' ? 'Heti nézet' : 'Havi nézet'}
            </Typography>
          </Box>

          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <ButtonGroup size="small" variant="outlined">
              <Button variant={viewMode === 'heti' ? 'contained' : 'outlined'} onClick={() => setViewMode('heti')}>
                Heti nézet
              </Button>
              <Button variant={viewMode === 'havi' ? 'contained' : 'outlined'} onClick={() => setViewMode('havi')}>
                Havi nézet
              </Button>
            </ButtonGroup>
          </Box>

          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <IconButton onClick={() => handlePeriodShift(-1)} aria-label="Előző időszak">
              <ChevronLeftIcon />
            </IconButton>

            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {periodTitle}
            </Typography>

            <IconButton onClick={() => handlePeriodShift(1)} aria-label="Következő időszak">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1 }}>
              {calendarWeekDayLabels.map((label) => (
                <Paper key={label} variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                </Paper>
              ))}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 1 }}>
              {periodDays.map((day) => {
                const dayKey = toLocalDateKey(day.date);
                const dayItems = groupedPeriodData[dayKey] || [];

                return (
                  <Paper
                    key={dayKey}
                    variant="outlined"
                    sx={{
                      p: 1,
                      minHeight: 170,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      opacity: day.inCurrentPeriod ? 1 : 0.45,
                      backgroundColor: day.inCurrentPeriod ? 'background.paper' : 'action.hover',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {day.date.toLocaleDateString('hu-HU', { month: '2-digit', day: '2-digit' })}
                      </Typography>
                      <Chip size="small" color="primary" label={`${dayItems.length} bejegyzés`} />
                    </Box>

                    <Divider />

                    {dayItems.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Nincs bejegyzés
                      </Typography>
                    ) : (
                      dayItems.map((item) => (
                        <Box
                          key={item.id}
                          onClick={() => {
                            setSelectedItem(item);
                            setIsPanelOpen(true);
                          }}
                          sx={{
                            p: 0.75,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>
                            {normalizeTime(item.kezdesIdo)} - {normalizeTime(item.befejezesIdo)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {item.felhasznaloNev || '-'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item.leiras}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {selectedItem && (
        <DetailPanel
          isOpen={isPanelOpen}
          data={selectedItem}
          columns={['datum', 'kezdesIdo', 'befejezesIdo', 'leiras', 'felhasznaloNev']}
          onClose={() => {
            setIsPanelOpen(false);
            setTimeout(() => setSelectedItem(null), 500);
          }}
          onEdit={handleEdit}
          onDelete={handleDelete}
          formatColumnName={formatColumnName}
          formatCellValue={formatCellValue}
          readonlyFields={['felhasznaloNev']}
          editPermission={editPermission}
          deletePermission={deletePermission}
        />
      )}

      <AddNaptarBejegyzesDialog
        open={addDialogOpen}
        title={addDialogTitle}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleCreate}
        defaultStartTime={defaultStartTime}
        defaultEndTime={defaultEndTime}
      />
    </Box>
  );
};

export default NaptarBejegyzesekContent;
